<template>
    <div class="lr-page">
        <!-- Header -->
        <div class="lr-header">
            <div class="lr-header-left">
                <div class="lr-icon-wrap"><i class="fas fa-business-time"></i></div>
                <div>
                    <h2 class="lr-title">Overtime Requests</h2>
                    <span class="lr-subtitle">{{ records.length }} {{ title.toLowerCase() }} request{{ records.length !== 1 ? 's' : '' }}</span>
                </div>
            </div>
        </div>

        <!-- Tabs + Search Row -->
        <div class="lr-toolbar">
            <div class="lr-tabs">
                <router-link to="/attendance-overtime-requests" :class="['lr-tab', { active: activeTabIndex === 0 }]">
                    <i class="fas fa-clock"></i> Pending
                </router-link>
                <router-link to="/attendance-overtime-requests-approved" :class="['lr-tab', { active: activeTabIndex === 1 }]">
                    <i class="fas fa-check-circle"></i> Approved
                </router-link>
                <router-link to="/attendance-overtime-requests-rejected" :class="['lr-tab', { active: activeTabIndex === 2 }]">
                    <i class="fas fa-times-circle"></i> Rejected
                </router-link>
            </div>
            <div class="lr-search-wrap">
                <SearchInput title="Search employee..." @search="fetchData" />
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="lr-loading">
            <i class="fas fa-spinner fa-spin"></i> Loading requests...
        </div>

        <!-- Empty -->
        <div v-else-if="!records.length" class="lr-empty">
            <div class="lr-empty-icon"><i class="fas fa-inbox"></i></div>
            <p class="lr-empty-title">No {{ title }} Requests</p>
            <p class="lr-empty-desc">There are no {{ title.toLowerCase() }} overtime requests at the moment.</p>
        </div>

        <!-- Records List -->
        <div v-else class="lr-list">
            <AttendanceRequestItem v-for="record of records" :key="record.id" :record="record" />
        </div>

        <Paginator @change="handlePageChange" :page="page" :totalPages="totalPages" />
    </div>
</template>

<script>
import { http } from "@/utils/http"
import AttendanceRequestItem from "../attendance-records/attendance-request-item.vue"
import Paginator from "@/components/other/Paginator.vue"
import SearchInput from "../../../components/SearchInput.vue"

export default {
    components: { SearchInput, AttendanceRequestItem, Paginator },
    data() {
        return {
            loading: false,
            records: [],
            page: 1,
            limit: 10,
            totalPages: 0,
        }
    },
    props: {
        extraRouteQuery: { type: String, default: "" },
        title: { type: String, default: "" },
        activeTabIndex: { type: Number, default: 0 },
    },
    mounted() {
        this.fetchData()
    },
    methods: {
        async fetchData(searchStr = "") {
            if (!!searchStr && this.page !== 1) this.page = 1
            if (this.loading) return
            this.loading = true
            try {
                const res = await http.get(`/attendance-records/overtime-requests?page=${this.page}&limit=${this.limit}&${this.extraRouteQuery}&search=${searchStr}`)
                this.records = res.data.result
                this.totalPages = res.data.totalPages
            } catch (err) { console.log("error in fetching overtime requests", err) }
            this.loading = false
        },
        async handlePageChange(newPage) {
            if (this.page != newPage) { this.page = newPage; this.fetchData() }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../../assets/variables.scss";
.lr-page { animation: lrFade .3s ease; }
@keyframes lrFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }

/* Header */
.lr-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; }
.lr-header-left { display:flex; align-items:center; gap:10px; }
.lr-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.lr-title { font-size:15px; font-weight:700; color:$gray800; margin:0; line-height:1.2; }
.lr-subtitle { font-size:11.5px; color:$gray400; font-weight:500; }

/* Toolbar */
.lr-toolbar { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:6px 10px; margin-bottom:14px; box-shadow:$shadowSm; flex-wrap:wrap; }
.lr-tabs { display:flex; gap:4px; }
.lr-tab { display:inline-flex; align-items:center; gap:5px; padding:7px 14px; border-radius:6px; font-size:12.5px; font-weight:600; color:$gray500; text-decoration:none; transition:all $transitionFast; i { font-size:11px; }
    &:hover { background:$gray50; color:$gray700; }
    &.active { background:$primaryColor; color:white; }
}
.lr-search-wrap { flex:0 0 220px; }

/* Loading */
.lr-loading { text-align:center; padding:40px 20px; color:$gray400; font-size:13px; font-weight:500; i { margin-right:6px; } }

/* Empty */
.lr-empty { text-align:center; padding:48px 20px; background:white; border:1px solid $gray100; border-radius:10px; }
.lr-empty-icon { width:48px; height:48px; border-radius:$radiusFull; background:$gray50; color:$gray300; display:flex; align-items:center; justify-content:center; font-size:20px; margin:0 auto 12px; }
.lr-empty-title { font-size:14px; font-weight:700; color:$gray700; margin:0 0 4px; }
.lr-empty-desc { font-size:12.5px; color:$gray400; margin:0; }

/* List */
.lr-list { display:grid; grid-template-columns:repeat(2, 1fr); gap:10px; margin-bottom:14px; }

@media(max-width:640px) {
    .lr-toolbar { flex-direction:column; align-items:stretch; }
    .lr-tabs { overflow-x:auto; }
    .lr-search-wrap { flex:auto; }
    .lr-list { grid-template-columns:1fr; }
}
</style>
